import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon,
  ChatBubbleLeftIcon,
  ShieldExclamationIcon,
  BoltIcon,
  UserPlusIcon,
  CogIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui';
import {
  getPriorityStyles,
  formatNotificationTime,
  isUnread,
  isActionable,
} from '../utils/notificationHelpers';

/**
 * Icon mapping
 */
const ICON_MAP = {
  BellIcon,
  ChatBubbleLeftIcon,
  ShieldExclamationIcon,
  BoltIcon,
  UserPlusIcon,
  CogIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
};

/**
 * Build dot-separated metadata string from a notification.
 * Segments: studentName · className (or ruleTitle) · timestamp
 * Only defined fields are included; empty segments are skipped.
 */
function buildMetaLine(notification) {
  const { metadata, createdAt } = notification;
  const segments = [
    metadata?.studentName,
    metadata?.className || metadata?.ruleTitle,
    formatNotificationTime(createdAt),
  ].filter(Boolean);
  return segments.join(' · ');
}

/**
 * NotificationItem Component
 *
 * Flat Linear-style row — no per-item card border or rounded corners.
 * Single unread signal: 2px left accent bar + bold title.
 * Priority expressed via icon colour only (no text badge).
 * Metadata as inline dot-separated text (no chip salad).
 * Action buttons are hover-revealed on desktop.
 */
const NotificationItem = ({
  notification,
  userId,
  onMarkAsRead,
  onDismiss,
  onNavigate,
  compact = false,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const Icon = ICON_MAP[notification.icon] || BellIcon;
  const priorityStyles = getPriorityStyles(notification.priority);
  const unread = isUnread(notification, userId);
  const actionable = isActionable(notification);

  const handleClick = async () => {
    if (loading) return;

    // Navigate if actionable. We deliberately do NOT mark-as-read here: the tray
    // shows unread only, so auto-reading on click would make the notification —
    // and its action buttons (Review / dismiss) — vanish the instant the user
    // engages it. Reading stays explicit via the check button or dismiss.
    if (actionable) {
      // Close the tray as we navigate. Leaving it open strands it under any
      // full-screen modal the destination opens (e.g. the adjudication Review
      // modal portals a z-999 inset-0 overlay over the whole viewport, the
      // navbar included), so clicks meant for the tray hit the overlay and the
      // tray looks frozen until you navigate away.
      onNavigate?.();
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAsRead = async (e) => {
    e.stopPropagation();
    if (loading || !unread) return;

    setLoading(true);
    try {
      await onMarkAsRead?.(notification.id);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
    setLoading(false);
  };

  const handleDismiss = async (e) => {
    e.stopPropagation();
    if (loading) return;

    setLoading(true);
    try {
      await onDismiss?.(notification.id);
    } catch (error) {
      console.error('Error dismissing:', error);
    }
    setLoading(false);
  };

  return (
    <div
      className={[
        // qc:allow tight-spacing — icon+content row uses list-compact spacing
        'group relative flex items-start gap-3',
        compact ? 'px-3 py-2.5' : 'px-4 py-3',
        unread ? 'bg-base-200/30 border-l-2 border-l-primary' : '',
        actionable ? 'cursor-pointer hover:bg-base-200/50' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {/* Icon — colour IS the priority signal (no text badge) */}
      <div className={`flex-shrink-0 ${priorityStyles.text}`}>
        <Icon className={compact ? 'w-5 h-5' : 'w-6 h-6'} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title — always font-normal; left accent bar is the sole unread signal */}
        <h4
          className={`${compact ? 'text-sm' : 'text-base'} font-normal`}
        >
          {notification.title}
        </h4>

        {/* Message */}
        {notification.message && (
          <p
            className={`text-sm text-base-content/70 mt-0.5 ${
              compact ? 'line-clamp-1' : 'line-clamp-2'
            }`}
          >
            {notification.message}
          </p>
        )}

        {/* Metadata + timestamp — inline dot-separated text; no chip salad */}
        <p className="text-xs text-base-content/60 mt-1">{buildMetaLine(notification)}</p>
      </div>

      {/* Action buttons — hover-reveal on desktop */}
      {/* qc:allow hover-only-affordance — spec requires hover-reveal on desktop */}
      {/* qc:allow tight-spacing — action buttons sit tight together */}
      <div className="flex items-start gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {/* Primary action */}
        {actionable && notification.actionLabel && (
          <Button
            className="btn-primary btn-sm"
            onClick={handleClick}
            disabled={loading}
          >
            {notification.actionLabel}
          </Button>
        )}

        {/* Mark as read (non-compact only, when unread) */}
        {unread && onMarkAsRead && !compact && (
          <Button
            className="btn-ghost btn-circle btn-sm"
            onClick={handleMarkAsRead}
            disabled={loading}
            title="Mark as read"
          >
            <CheckIcon className="w-4 h-4" />
          </Button>
        )}

        {/* Dismiss */}
        {onDismiss && (
          <Button
            className="btn-ghost btn-circle btn-sm"
            onClick={handleDismiss}
            disabled={loading}
            title="Dismiss"
          >
            <XMarkIcon className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
